import { useState, useEffect, useCallback, useMemo } from "react";

export function useSimulationData(apiBaseUrl, authToken) {
  const [allSimulations, setAllSimulations] = useState([]);
  const [recentSimulationsWithEval, setRecentSimulationsWithEval] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  // Fetch recent simulations for a specific scenario (Ad-hoc request)
  const fetchRecentSimulationsForScenario = useCallback(
    async (scenarioId, limit = 5) => {
      if (!authToken || !apiBaseUrl) return [];

      try {
        const response = await fetch(
          `${apiBaseUrl}/recent_simulations_with_evaluation?scenario_id=${scenarioId}&limit=${limit}`,
          {
            headers: { Authorization: `Token ${authToken}` },
          }
        );
        return await response.json();
      } catch (error) {
        console.error("Error fetching recent simulations for scenario:", error);
        return [];
      }
    },
    [authToken, apiBaseUrl]
  );

  // Main refresh function (updates global simulation lists)
  const refreshSimulations = useCallback(async () => {
    if (!authToken || !apiBaseUrl) return;
    
    setLoading(true);
    try {
      const headers = { Authorization: `Token ${authToken}` };

      const [simulationsResponse, recentResponse] = await Promise.all([
        fetch(`${apiBaseUrl}/optimized_get_simulations`, { headers }),
        fetch(`${apiBaseUrl}/recent_simulations_with_evaluation?limit=5`, { headers }),
      ]);

      const [simulationsData, recentData] = await Promise.all([
        simulationsResponse.json(),
        recentResponse.json(),
      ]);

      setAllSimulations(simulationsData);
      setRecentSimulationsWithEval(recentData);
      setLastFetch(Date.now());
    } catch (error) {
      console.error("Error refreshing simulations:", error);
    } finally {
      setLoading(false);
    }
  }, [authToken, apiBaseUrl]);

  // Trigger initial fetch
  useEffect(() => {
    refreshSimulations();
  }, [refreshSimulations]);

  // Compute derived state efficiently
  const simulationsData = useMemo(() => {
    const completed = allSimulations.filter(
      (sim) => sim.status === "completed" && sim.evaluation !== null
    );

    const recentCompleted = [...completed]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);

    const inProgress = allSimulations.filter(
      (sim) => sim.status !== "completed"
    );

    return {
      all: allSimulations,
      completed,
      recentCompleted,
      inProgress,
      loading: loading,
      lastFetch: lastFetch,
      recentWithEvaluation: recentSimulationsWithEval,
      recentEvaluationLoading: loading,
    };
  }, [allSimulations, recentSimulationsWithEval, loading, lastFetch]);

  return {
    simulationsData,
    refreshSimulations,
    fetchRecentSimulationsForScenario,
  };
}